/**
 * Mar Amoli — Customer preferences (V2)
 *
 * Storage (todo localStorage, sin servidor):
 *   localStorage["ma-preferences"] = JSON {
 *     skin_type, hair_type, aromas[], benefits[], frequency,
 *     avoid, phone_alt, birthday_md ("MM-DD"), shower_time,
 *     scent_intensity, packaging_pref, ...
 *   }
 *
 * Question pool: window.MaPreferences.questions describe TODAS las
 * preguntas que podemos hacer al cliente. La card "Pregunta del día"
 * en el dashboard usa nextUnanswered() para mostrar UNA por visita.
 *
 * El form completo en /pages/mi-informacion sigue siendo la vista
 * canónica (el cliente puede llenar todo de golpe ahí).
 *
 * "Enviar al equipo" arma email rico (perfil + historial) y lo
 * submitea con el contact form nativo de Shopify (no app needed).
 *
 * API pública: window.MaPreferences.{get, set, list, has, questions,
 *   nextUnanswered, answeredCount, totalCount, answer}
 */
(function () {
  "use strict";
  const STORAGE_KEY = "ma-preferences";

  // ── Banco de preguntas ────────────────────────────────────────
  // type: 'radio' | 'multi' | 'date-md' | 'text'
  // Un value vacío en el storage cuenta como NO respondida.
  const QUESTIONS = [
    {
      key: "skin_type",
      type: "radio",
      emoji: "🌿",
      eyebrow: "Tu piel",
      question: "¿Cuál describe mejor tu piel hoy?",
      hint: "Puede cambiar con el clima, no te preocupes.",
      options: [
        { v: "normal", l: "Normal" },
        { v: "mixta", l: "Mixta" },
        { v: "grasa", l: "Grasa" },
        { v: "seca", l: "Seca" },
        { v: "sensible", l: "Sensible" },
      ],
    },
    {
      key: "aromas",
      type: "multi",
      emoji: "🪻",
      eyebrow: "Tus aromas",
      question: "¿Qué aromas te llaman más la atención?",
      hint: "Elige todos los que te gusten — sin compromiso.",
      options: [
        { v: "floral", l: "Floral" },
        { v: "citrico", l: "Cítrico" },
        { v: "madera", l: "Madera" },
        { v: "herbal", l: "Herbal" },
        { v: "mentolado", l: "Mentolado" },
        { v: "especiado", l: "Especiado" },
        { v: "dulce", l: "Dulce" },
        { v: "sin-aroma", l: "Sin aroma" },
      ],
    },
    {
      key: "benefits",
      type: "multi",
      emoji: "✨",
      eyebrow: "Tu prioridad",
      question: "¿Qué buscas en un producto?",
      hint: "Esto nos ayuda a recomendarte mejor.",
      options: [
        { v: "hidratante", l: "Hidratar" },
        { v: "exfoliante", l: "Exfoliar" },
        { v: "aclarante", l: "Luminosidad" },
        { v: "refrescante", l: "Refrescar" },
        { v: "relajante", l: "Relajar" },
        { v: "anti-acne", l: "Piel grasa o mixta" },
        { v: "anti-edad", l: "Piel madura" },
      ],
    },
    {
      key: "hair_type",
      type: "radio",
      emoji: "💁",
      eyebrow: "Tu cabello",
      question: "¿Cómo es tu cabello?",
      hint: "Si no usas shampoo de Mariana aún, igual cuéntanos.",
      options: [
        { v: "normal", l: "Normal" },
        { v: "graso", l: "Graso" },
        { v: "seco", l: "Seco" },
        { v: "mixto", l: "Mixto" },
        { v: "rizado", l: "Rizado" },
        { v: "lacio", l: "Lacio" },
        { v: "tenido", l: "Teñido" },
      ],
    },
    {
      key: "shower_time",
      type: "radio",
      emoji: "🚿",
      eyebrow: "Tu ritual",
      question: "¿En qué momento del día sueles bañarte?",
      hint: "Influye en lo que sentirás mejor en la piel.",
      options: [
        { v: "manana", l: "Por la mañana" },
        { v: "noche", l: "Por la noche" },
        { v: "ambos", l: "Ambos" },
        { v: "varia", l: "Varía mucho" },
      ],
    },
    {
      key: "frequency",
      type: "radio",
      emoji: "📅",
      eyebrow: "Tu frecuencia",
      question: "¿Cada cuánto compras productos de cuidado?",
      hint: "Para sugerirte cuándo suscribirte.",
      options: [
        { v: "mensual", l: "Cada mes" },
        { v: "bimestral", l: "Cada 2 meses" },
        { v: "trimestral", l: "Cada 3 meses" },
        { v: "ocasional", l: "Cuando se acaba" },
      ],
    },
    {
      key: "scent_intensity",
      type: "radio",
      emoji: "👃",
      eyebrow: "Intensidad",
      question: "¿Qué tan fuerte te gusta el aroma?",
      hint: "Algunos jabones son más sutiles, otros marcan presencia.",
      options: [
        { v: "suave", l: "Suave" },
        { v: "medio", l: "Medio" },
        { v: "fuerte", l: "Fuerte" },
      ],
    },
    {
      key: "birthday_md",
      type: "date-md",
      emoji: "🎂",
      eyebrow: "Tu cumpleaños",
      question: "¿Cuándo es tu cumpleaños?",
      hint: "Solo mes y día — para mandarte algo especial 🎁",
    },
    {
      key: "avoid",
      type: "text",
      emoji: "⚠️",
      eyebrow: "Tus alertas",
      question: "¿Hay algún ingrediente o aroma que evitas?",
      hint: "Alergias, embarazo, reacciones previas — escríbelo libre.",
      placeholder: "Ej: lavanda, parabenos, fragancias sintéticas",
    },
    {
      key: "packaging_pref",
      type: "radio",
      emoji: "📦",
      eyebrow: "Tu empaque",
      question: "¿Prefieres recibir tu jabón con o sin cajita?",
      hint: "Sin cajita ahorra papel y baja un poco el precio.",
      options: [
        { v: "con-cajita", l: "Con cajita (regalo)" },
        { v: "sin-cajita", l: "Sin cajita (eco)" },
        { v: "no-importa", l: "Da igual" },
      ],
    },
    {
      key: "phone_alt",
      type: "text",
      emoji: "📱",
      eyebrow: "Contacto",
      question: "¿Tienes un WhatsApp donde podamos avisarte cuando lleguen tus pedidos?",
      hint: "Opcional — solo lo usamos para tu pedido.",
      placeholder: "+52 ...",
    },
  ];

  function getPrefs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function setPrefs(obj) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (err) {
      console.warn("[ma-prefs] localStorage write failed", err);
    }
    document.dispatchEvent(
      new CustomEvent("ma:preferences:changed", { detail: { prefs: obj } }),
    );
  }

  function isAnswered(key) {
    const p = getPrefs();
    const v = p[key];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return String(v).trim() !== "";
  }

  function nextUnanswered() {
    return QUESTIONS.find((q) => !isAnswered(q.key)) || null;
  }

  function answeredCount() {
    return QUESTIONS.filter((q) => isAnswered(q.key)).length;
  }

  function answer(key, value) {
    const p = getPrefs();
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
      delete p[key];
    } else {
      p[key] = value;
    }
    setPrefs(p);
  }

  // Public API
  window.MaPreferences = {
    get: getPrefs,
    set: setPrefs,
    has: (key) => isAnswered(key),
    list: () => {
      const p = getPrefs();
      const out = {};
      for (const k in p) {
        out[k] = Array.isArray(p[k]) ? p[k].slice() : p[k];
      }
      return out;
    },
    questions: QUESTIONS,
    nextUnanswered,
    answeredCount,
    totalCount: QUESTIONS.length,
    answer,
  };

  // ── DOM helpers (createElement-based, XSS-safe) ───────────────
  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach((k) => {
        if (k === "className") node.className = attrs[k];
        else if (k === "style") node.setAttribute("style", attrs[k]);
        else if (k.startsWith("data") || k === "name" || k === "type" || k === "value" || k === "placeholder" || k === "href" || k === "role" || k === "aria-hidden")
          node.setAttribute(k, attrs[k]);
        else node[k] = attrs[k];
      });
    }
    if (children == null) return node;
    if (!Array.isArray(children)) children = [children];
    children.forEach((c) => {
      if (c == null) return;
      if (typeof c === "string") node.appendChild(document.createTextNode(c));
      else node.appendChild(c);
    });
    return node;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  // ── Helpers de labels desde QUESTIONS ─────────────────────────
  function labelFor(key, value) {
    const q = QUESTIONS.find((x) => x.key === key);
    if (!q || !q.options) return value;
    const opt = q.options.find((o) => o.v === value);
    return opt ? opt.l : value;
  }

  function labelsFor(key, values) {
    if (!Array.isArray(values)) return labelFor(key, values);
    return values.map((v) => labelFor(key, v)).join(", ");
  }

  // ── Format prefs como texto rico para email a Mariana ─────────
  function formatPrefsAsText(prefs) {
    const lines = ["Resumen de preferencias del cliente:", ""];
    QUESTIONS.forEach((q) => {
      const v = prefs[q.key];
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;
      const label = q.eyebrow + " — " + q.question.replace(/¿|\?/g, "");
      const value = q.type === "multi" ? labelsFor(q.key, v) : labelFor(q.key, v);
      lines.push("• " + label + ": " + value);
    });
    if (lines.length === 2) {
      lines.push("(Sin preferencias guardadas todavía)");
    }
    const meta = document.querySelector("[data-ma-prefs-customer]");
    if (meta) {
      lines.push("", "— Cliente —");
      if (meta.dataset.name) lines.push("Nombre: " + meta.dataset.name);
      if (meta.dataset.email) lines.push("Email: " + meta.dataset.email);
      if (meta.dataset.orders) lines.push("Pedidos hechos: " + meta.dataset.orders);
      if (meta.dataset.lastOrder) lines.push("Último pedido: " + meta.dataset.lastOrder);
    }
    lines.push("", "— Enviado desde la cuenta Mar Amoli");
    return lines.join("\n");
  }

  // ── FORM completo en mi-informacion ───────────────────────────
  const form = document.querySelector("[data-ma-prefs-form]");
  if (form) {
    const statusWrap = document.querySelector("[data-ma-prefs-status]");
    const statusText = document.querySelector("[data-ma-prefs-status-text]");

    function setStatus(text, state) {
      if (!statusWrap) return;
      statusWrap.classList.remove("is-saved", "is-saving");
      if (state) statusWrap.classList.add(state);
      if (statusText) statusText.textContent = text;
    }

    function hydrateForm() {
      const prefs = getPrefs();
      Object.keys(prefs).forEach((key) => {
        const value = prefs[key];
        const inputs = form.querySelectorAll('[name="' + key + '"]');
        if (inputs.length === 0) return;
        inputs.forEach((input) => {
          if (input.type === "checkbox") {
            if (Array.isArray(value)) input.checked = value.includes(input.value);
          } else if (input.type === "radio") {
            input.checked = input.value === value;
          } else {
            input.value = value;
          }
        });
      });
      if (Object.keys(prefs).length > 0) setStatus("Preferencias guardadas", "is-saved");
      else setStatus("Sin preferencias guardadas aún", null);
    }

    function readForm() {
      const formData = new FormData(form);
      const prefs = {};
      formData.forEach((value, key) => {
        const stringValue = String(value || "").trim();
        if (!stringValue) return;
        if (key === "aromas" || key === "benefits") {
          if (!prefs[key]) prefs[key] = [];
          prefs[key].push(stringValue);
        } else {
          prefs[key] = stringValue;
        }
      });
      return prefs;
    }

    function saveAndStatus() {
      setStatus("Guardando…", "is-saving");
      const prefs = readForm();
      setPrefs(prefs);
      setTimeout(() => setStatus("Preferencias guardadas", "is-saved"), 250);
    }

    let saveTimeout;
    function scheduleSave() {
      clearTimeout(saveTimeout);
      setStatus("Guardando…", "is-saving");
      saveTimeout = setTimeout(saveAndStatus, 350);
    }

    form.addEventListener("change", scheduleSave);
    form.addEventListener("input", (e) => {
      if (e.target.matches("textarea, input[type='tel']")) scheduleSave();
    });

    hydrateForm();
  }

  // ── Send to team (en mi-informacion) ──────────────────────────
  const sendBtn = document.querySelector("[data-ma-prefs-send]");
  const sendStatus = document.querySelector("[data-ma-prefs-send-status]");
  const contactForm = document.getElementById("PrefsContactForm");
  const contactBody = document.querySelector("[data-ma-prefs-body]");

  if (sendBtn && contactForm && contactBody) {
    sendBtn.addEventListener("click", () => {
      const prefs = getPrefs();
      contactBody.value = formatPrefsAsText(prefs);
      sendBtn.disabled = true;
      sendBtn.textContent = "Enviando…";
      contactForm.submit();
    });
  }

  if (document.querySelector("[data-ma-prefs-posted]")) {
    if (sendStatus) {
      sendStatus.removeAttribute("hidden");
      sendStatus.textContent = "✓ Tus preferencias se enviaron al equipo. Mariana las verá pronto.";
    }
  }

  // ── Card "Pregunta del día" en dashboard ──────────────────────
  const dailySlot = document.querySelector("[data-ma-daily-q]");
  if (dailySlot) {
    renderDaily();
    document.addEventListener("ma:preferences:changed", renderDaily);
  }

  function buildProgress(done, total) {
    const pct = Math.round((done / total) * 100);
    const bar = el("span", { style: "width:" + pct + "%" });
    const barWrap = el("div", { className: "ma-daily__progress-bar" }, bar);
    const txt = el(
      "div",
      { className: "ma-daily__progress-text" },
      done + "/" + total + " respondidas · sigue construyendo tu perfil",
    );
    return el("div", { className: "ma-daily__progress", "aria-hidden": "true" }, [barWrap, txt]);
  }

  function renderDaily() {
    const q = nextUnanswered();
    const total = QUESTIONS.length;
    const done = answeredCount();
    clear(dailySlot);

    if (!q) {
      const emoji = el("div", { className: "ma-daily__emoji", "aria-hidden": "true" }, "🌿");
      const eyebrow = el("div", { className: "ma-daily__eyebrow" }, "Tu perfil");
      const h2 = el("h2", { className: "ma-daily__q" }, "Tu perfil está completo, gracias.");
      const link = el("a", { href: "/pages/mi-informacion" }, "Mi información");
      const hint = el("p", { className: "ma-daily__hint" }, [
        "Mariana puede recomendarte mejor con esto. Si algo cambia, edita desde ",
        link,
        ".",
      ]);
      const progress = buildProgress(done, total);
      const body = el("div", { className: "ma-daily__body" }, [eyebrow, h2, hint, progress]);
      dailySlot.appendChild(el("div", { className: "ma-daily ma-daily--complete" }, [emoji, body]));
      return;
    }

    const emoji = el("div", { className: "ma-daily__emoji", "aria-hidden": "true" }, q.emoji || "🌿");
    const eyebrow = el("div", { className: "ma-daily__eyebrow" }, q.eyebrow);
    const h2 = el("h2", { className: "ma-daily__q" }, q.question);
    const hint = q.hint ? el("p", { className: "ma-daily__hint" }, q.hint) : null;

    let inputBlock;
    let footerSaveBtn = null;
    if (q.type === "radio") {
      const chips = q.options.map((o) => {
        const input = el("input", {
          type: "radio",
          name: q.key,
          value: o.v,
          "data-ma-daily-input": "1",
        });
        input.addEventListener("change", () => {
          if (input.checked) answer(q.key, input.value);
        });
        return el("label", { className: "ma-daily__chip" }, [input, el("span", null, o.l)]);
      });
      inputBlock = el("div", { className: "ma-daily__chips", role: "radiogroup" }, chips);
    } else if (q.type === "multi") {
      const chips = q.options.map((o) => {
        const input = el("input", {
          type: "checkbox",
          name: q.key,
          value: o.v,
          "data-ma-daily-input": "1",
        });
        return el("label", { className: "ma-daily__chip" }, [input, el("span", null, o.l)]);
      });
      inputBlock = el("div", { className: "ma-daily__chips" }, chips);
      footerSaveBtn = el("button", { type: "button", className: "ma-daily__save" }, "Guardar respuesta →");
      footerSaveBtn.addEventListener("click", () => {
        const checked = Array.from(
          dailySlot.querySelectorAll('input[name="' + q.key + '"]:checked'),
        ).map((i) => i.value);
        if (checked.length > 0) answer(q.key, checked);
      });
    } else if (q.type === "date-md") {
      const monthSel = el("select", { name: q.key + "_m", "data-ma-daily-month": "1" });
      monthSel.appendChild(el("option", { value: "" }, "Mes"));
      ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].forEach((m, i) => {
        monthSel.appendChild(el("option", { value: String(i + 1).padStart(2, "0") }, m));
      });
      const daySel = el("select", { name: q.key + "_d", "data-ma-daily-day": "1" });
      daySel.appendChild(el("option", { value: "" }, "Día"));
      for (let d = 1; d <= 31; d++) {
        daySel.appendChild(el("option", { value: String(d).padStart(2, "0") }, String(d)));
      }
      const saveBtn = el("button", { type: "button", className: "ma-daily__save" }, "Guardar →");
      saveBtn.addEventListener("click", () => {
        if (monthSel.value && daySel.value) answer(q.key, monthSel.value + "-" + daySel.value);
      });
      inputBlock = el("div", { className: "ma-daily__date" }, [monthSel, daySel, saveBtn]);
    } else if (q.type === "text") {
      const input = el("input", {
        type: "text",
        name: q.key,
        placeholder: q.placeholder || "",
        "data-ma-daily-input": "1",
      });
      const saveBtn = el("button", { type: "button", className: "ma-daily__save" }, "Guardar →");
      saveBtn.addEventListener("click", () => {
        const v = input.value.trim();
        if (v) answer(q.key, v);
      });
      inputBlock = el("div", { className: "ma-daily__text-row" }, [input, saveBtn]);
    }

    const progress = buildProgress(done, total);
    const bodyChildren = [eyebrow, h2];
    if (hint) bodyChildren.push(hint);
    bodyChildren.push(inputBlock);
    if (footerSaveBtn) {
      bodyChildren.push(
        el("div", { className: "ma-daily__footer-row" }, [footerSaveBtn, progress]),
      );
    } else {
      bodyChildren.push(progress);
    }
    const body = el("div", { className: "ma-daily__body" }, bodyChildren);
    dailySlot.appendChild(el("div", { className: "ma-daily" }, [emoji, body]));
  }

  // ── Recomendaciones para ti ───────────────────────────────────
  const recoRoot = document.querySelector("[data-ma-reco]");
  if (recoRoot) {
    renderRecos();
    document.addEventListener("ma:preferences:changed", renderRecos);
  }

  function renderRecos() {
    const prefs = getPrefs();
    const wantTags = new Set();
    if (Array.isArray(prefs.aromas))
      prefs.aromas.forEach((a) => wantTags.add("aroma-" + a));
    if (Array.isArray(prefs.benefits))
      prefs.benefits.forEach((b) => wantTags.add("beneficio-" + b));
    if (prefs.skin_type) wantTags.add("piel-" + prefs.skin_type);
    if (prefs.hair_type) wantTags.add("cabello-" + prefs.hair_type);

    const items = Array.from(recoRoot.querySelectorAll("[data-ma-reco-item]"));
    const limit = parseInt(recoRoot.dataset.maRecoLimit || "3", 10);
    const heading = recoRoot.querySelector("[data-ma-reco-heading]");

    if (wantTags.size === 0) {
      items.forEach((node, i) => { node.style.display = i < limit ? "" : "none"; });
      if (heading) heading.textContent = "Mariana recomienda";
      return;
    }

    const scored = items.map((node) => {
      const tags = (node.dataset.maTags || "").split("|").filter(Boolean);
      let score = 0;
      tags.forEach((t) => { if (wantTags.has(t)) score += 1; });
      return { node, score };
    });
    scored.sort((a, b) => b.score - a.score);

    const anyMatch = scored.some((s) => s.score > 0);
    if (!anyMatch) {
      items.forEach((node, i) => { node.style.display = i < limit ? "" : "none"; });
      if (heading) heading.textContent = "Mariana recomienda";
      return;
    }

    scored.forEach((s, i) => {
      s.node.style.display = i < limit && s.score > 0 ? "" : "none";
    });
    if (heading) heading.textContent = "Para ti";
  }

  // ── Mi rutina — botón "+" agregar al carrito ─────────────────
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-ma-routine-add]");
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;
    btn.disabled = true;
    try {
      const formData = new FormData();
      formData.append("id", String(variantId));
      formData.append("quantity", "1");
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", window.location.pathname);
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("cart add failed");
      const result = await res.json();
      if (result.sections && result.sections["cart-drawer"]) {
        const drawer = document.querySelector("ma-cart-drawer");
        if (drawer) {
          const doc = new DOMParser().parseFromString(
            result.sections["cart-drawer"],
            "text/html",
          );
          const newDrawer = doc.querySelector("ma-cart-drawer");
          if (newDrawer) drawer.replaceChildren(...Array.from(newDrawer.childNodes));
        }
      }
      btn.setAttribute("data-added", "1");
      if (window.MaCart?.agregado) window.MaCart.agregado(); else if (window.MaCart && typeof window.MaCart.open === "function") window.MaCart.open();
      setTimeout(() => {
        btn.removeAttribute("data-added");
        btn.disabled = false;
      }, 1200);
    } catch (err) {
      btn.disabled = false;
    }
  });
})();
